/**
 * Audio Material Component
 *
 * Displays audio files with HTML5 audio player.
 */

import { useTranslation } from 'react-i18next';
import { useMaterialUrl, type Material } from '@immopersian/common';
import { LoadingSpinner } from './shared/LoadingSpinner';
import { ErrorMessage } from './shared/ErrorMessage';
import { MaterialDescription } from './shared/MaterialDescription';
import { AudioIcon } from './shared/Icons';

interface AudioMaterialProps {
  material: Material;
}

export function AudioMaterial({ material }: AudioMaterialProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMaterialUrl(material.id, material.type);

  if (isLoading) {
    return <LoadingSpinner message={t('courses.loadingAudio')} />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  return (
    <>
      <MaterialDescription material={material} />
      <div className="bg-white rounded-lg border border-brand-beige p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-brand-beige rounded-lg flex items-center justify-center flex-shrink-0">
            <AudioIcon />
          </div>
          <p className="text-sm text-brand-dark">{t('courses.audioFile')}</p>
        </div>
        <audio controls className="w-full">
          <source src={data?.url || ''} />
          Your browser does not support the audio element.
        </audio>
      </div>
    </>
  );
}
